import { gql } from "@apollo/client";
import { useMutation } from "@apollo/client/react";
import {
  DSButton,
  DSDropzone,
} from "@components/design-system/molecules";
import { faCloudArrowUp } from "@fortawesome/pro-solid-svg-icons";
import mixpanel from "mixpanel-browser";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { Controller, useController } from "react-hook-form";

import { ErrorMessage } from "./Input";
import { useUploadProgress } from "../../../hooks";
import { submitAttachments } from "../../attachments/attachment-utils";

const UPLOAD_NOTE_ATTACHMENTS = gql`
  mutation uploadNoteAttachments(
    $noteUuid: String!
    $attachments: [AttachmentInput]!
    $createdAt: DateTime!
  ) {
    uploadNoteAttachments(
      noteUuid: $noteUuid
      attachments: $attachments
      createdAt: $createdAt
    ) {
      ok
    }
  }
`;

export function ProgressBar({ uploadProgress, width = "w-44" }) {
  return (
    <div
      className={`${width} bg-tertiary rounded-full h-2 overflow-hidden`}
    >
      <div
        className="bg-brand h-full rounded-full transition-all"
        style={{ width: `${uploadProgress}%` }}
      />
    </div>
  );
}

export default function AttachmentSelection({
  noteUuid,
  onUploadComplete,
  isForNoteForm = true,
  control,
  acceptedFiles = "image/*, video/*, .pdf, .doc, .docx, application/pdf, application/msword, application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  shouldAcceptMultipleFiles = true,
  inputAction = null,
  clearAction = null,
  shouldClearUploads = null,
  setShouldClearUploads = null,
}) {
  const [selectedFileNames, setSelectedFileNames] = useState([]);
  const [selectedRawFiles, setSelectedRawFiles] = useState(null);
  const [err, setErr] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  const { field } = useController({
    name: "attachments",
    control,
  });

  const {
    currentFileName,
    percentageUploaded,
    setPercentageUploaded,
    setCurrentFileName,
  } = useUploadProgress();

  const [uploadNoteAttachments] = useMutation(
    UPLOAD_NOTE_ATTACHMENTS,
  );

  useEffect(() => {
    if (
      !shouldClearUploads ||
      !field ||
      typeof setShouldClearUploads !== "function"
    ) {
      return;
    }

    field.onChange(null);
    setSelectedFileNames([]);
    setSelectedRawFiles(null);
    if (clearAction) {
      clearAction();
    }
    setShouldClearUploads(false);
  }, [shouldClearUploads, field, setShouldClearUploads, clearAction]);

  function validateFiles(files) {
    setErr(null);
    if (!files?.length) {
      return false;
    }
    const fileTypeArray = acceptedFiles.split(", ");

    for (let i = 0; i < files.length; i++) {
      if (files[i]?.size / 1024 / 1024 > 2048) {
        setErr("File size exceeds the limit of 2 GB");
        return false;
      }
      let fileTypeCheck = files[i]?.type;

      if (files[i]?.type.split("/")[0] === "image") {
        fileTypeCheck = "image/*";
      }

      if (files[i]?.type.split("/")[0] === "video") {
        fileTypeCheck = "video/*";
      }

      if (!fileTypeArray.includes(fileTypeCheck)) {
        setErr(`File type not accepted: ${files[i]?.type}`);
        return false;
      }
    }

    if (inputAction) {
      inputAction();
    }
    return true;
  }

  async function handleUpload() {
    setIsUploading(true);
    setErr(null);
    try {
      const uploadedFiles = await submitAttachments({
        files: selectedRawFiles,
        setPercentageUploaded,
        setCurrentFileName,
      });

      if (uploadedFiles) {
        await uploadNoteAttachments({
          variables: {
            createdAt: moment().format(),
            noteUuid,
            attachments: uploadedFiles,
          },
        });
        setSelectedFileNames([]);
        setSelectedRawFiles(null);
        onUploadComplete?.();
      }
    } catch {
      setErr("Upload failed. Please try again.");
    } finally {
      setIsUploading(false);
    }
  }

  const selectedFiles = selectedFileNames.map((name) => ({
    name,
    progress:
      name === currentFileName ? percentageUploaded : undefined,
  }));

  const hasFilesReady = noteUuid && selectedRawFiles?.length > 0;

  return (
    <>
      <Controller
        control={control}
        render={({ field: { onChange } }) => (
          <DSDropzone
            dataTestId="attachment-input"
            accept={acceptedFiles}
            multiple={shouldAcceptMultipleFiles}
            disabled={
              noteUuid
                ? isUploading
                : isForNoteForm && !!percentageUploaded
            }
            label={`Upload ${!shouldAcceptMultipleFiles ? "a file" : "one or more files"}`}
            helperText="Drag and drop or click to browse"
            selectedFiles={selectedFiles}
            onClear={() => {
              onChange(null);
              setSelectedFileNames([]);
              setSelectedRawFiles(null);
            }}
            onFilesSelected={(files) => {
              mixpanel.track("Uploaded Attachment(s)");
              if (validateFiles(files)) {
                const fileArray = Array.from(files);
                setSelectedFileNames(fileArray.map((f) => f.name));
                if (noteUuid) {
                  setSelectedRawFiles(fileArray);
                } else {
                  onChange(files);
                }
              }
            }}
          />
        )}
        name="attachments"
      />

      {hasFilesReady && (
        <div className="flex justify-end mt-sm">
          <DSButton
            icon={faCloudArrowUp}
            onClick={handleUpload}
            disabled={isUploading}
            dataTestId="upload-attachments-button"
          >
            {isUploading ? "Uploading..." : "Upload"}
          </DSButton>
        </div>
      )}

      {err && <ErrorMessage message={err} />}
    </>
  );
}
